<template>
  <div>
    <b-form
      class="job-activities"
      @keydown="form.errors.clear($event.target.name)"
      @submit.prevent="onSubmit"
    >
      <template v-if="!readonly">
        <template v-if="$store.state.jobs.currentJob.brand_id">
          <textarea
            v-model.trim="form.detail"
            class="job-note form-control"
            name="detail"
            placeholder="Type a job note here..."
            rows="3"
          />

          <b-card-footer style="border-top: 0">
            <div class="d-flex">
              <button
                v-if="!editingUpload"
                :disabled="form.notReady() || (!form.detail && !form.file)"
                class="btn btn-primary btn-sm mr-3"
              >
                <font-awesome-icon v-if="form.busy" :icon="['fal', 'spinner']" spin />
                <font-awesome-icon v-else :icon="['fal', 'plus-circle']" />
                Add Text
              </button>

              <file-uploader
                v-show="form.detail.length === 0"
                :data="{ job_id: currentJobId, type: 'attachment' }"
                :file-url="form.file"
                post-action="job-notes"
                @editing="editingUpload = true"
                @notEditing="editingUpload = false"
                @uploaded="fileUploaded"
              >
                <font-awesome-icon :icon="['fal', 'cloud-upload']" />
                Attach File
              </file-uploader>
            </div>
          </b-card-footer>
        </template>
        <template v-else>
          <div class="text-muted">Cannot create notes until the job is created.</div>
        </template>
      </template>
      <template v-else>
        <header class="job-header row">
          <div class="col">
            <b>JOB #{{ currentJobId }}</b>
          </div>
        </header>
      </template>

      <div class="row no-print">
        <div class="col-8">
          <div class="mt-3">
            <b-button variant="info" @click.prevent="createBeforeAndAfter">
              Create Before And After
            </b-button>
          </div>

          <div class="mt-3">
            <b-button-group>
              <b-button :variant="type === 'all' ? 'success' : ''" @click.prevent="type = 'all'">
                All
              </b-button>
              <b-button
                :variant="type === 'notes' ? 'warning' : ''"
                @click.prevent="type = 'notes'"
              >
                Notes
              </b-button>
              <b-button :variant="type === 'files' ? 'danger' : ''" @click.prevent="type = 'files'">
                Files
              </b-button>
              <b-button :variant="type === 'log' ? 'info' : ''" @click.prevent="type = 'log'">
                Log
              </b-button>
            </b-button-group>
          </div>

          <div v-if="type === 'log'" class="mt-2">
            <b-button-group size="sm">
              <b-button
                :variant="subType === 'all' ? 'success' : ''"
                @click.prevent="subType = 'all'"
              >
                All
              </b-button>
              <b-button
                :variant="subType === 'job' ? 'warning' : ''"
                @click.prevent="subType = 'job'"
              >
                Job
              </b-button>
              <b-button
                :variant="subType === 'appointment' ? 'danger' : ''"
                @click.prevent="subType = 'appointment'"
              >
                Appointment
              </b-button>
              <b-button
                :variant="subType === 'invoice' ? 'info' : ''"
                @click.prevent="subType = 'invoice'"
              >
                Invoice
              </b-button>
            </b-button-group>
          </div>
        </div>
        <div class="col-4 text-right pt-3">
          <button v-if="readonly" class="btn btn-secondary no-print" @click.prevent="print">
            <font-awesome-icon :icon="['fal', 'file-pdf']" />
            Print
          </button>

          <b-button v-else variant="secondary" @click.prevent="exportLog">Export Log</b-button>
        </div>
      </div>

      <ul
        v-show="activities.length > 0"
        :style="readonly ? '' : 'max-height: 552px'"
        class="list-group overflow-y-auto mt-3"
      >
        <!-- eslint-disable -->
        <li
          v-for="activity in activities"
          v-if="shouldShow(activity)"
          :key="activity.id + activity.action"
          class="list-group-item activity no-break"
        >
          <div class="row no-gutters">
            <div class="col-auto">
              <div class="highlight text-center">
                <font-awesome-icon
                  :class="getOperationClass(activity)"
                  :icon="getOperationIcon(activity)"
                />
              </div>
            </div>
            <div class="col">
              <activity-item :activity="activity"></activity-item>
            </div>
          </div>
        </li>
      </ul>

      <div class="mt-3">
        <button
          v-if="!readonly"
          :disabled="!lastPage || this.page >= lastPage || form.busy || loadingMore"
          class="btn btn-primary"
          @click.prevent="loadMore"
        >
          <font-awesome-icon v-if="form.busy || loadingMore" :icon="['fal', 'spinner']" spin />
          <font-awesome-icon v-else :icon="['fal', 'caret-down']" />
          Load more
        </button>
      </div>
    </b-form>

    <before-and-after ref="beforeAndAfter" :job-id="currentJobId" />
  </div>
</template>

<script type="text/babel">
import ActivityItem from "./ActivityItem.vue";
import format from "../../../mixins/format";
import EventBus from "../../../imports/event-bus";
import FileUploader from "../../../components/common/FileUploader.vue";
import BeforeAndAfter from "../../../components/common/BeforeAndAfter.vue";

const initialState = {
  id: "",
  job_id: "",
  detail: "",
  file: "",
};

export default {
  components: {
    ActivityItem,
    FileUploader,
    BeforeAndAfter,
  },
  mixins: [format],

  props: {
    currentJobId: {
      type: Number,
      required: true,
    },

    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      initialised: false,
      type: this.readonly ? "log" : "notes",
      subType: "all",
      activities: [],

      perPage: 100,
      page: 0,
      lastPage: null,
      loadingMore: false,
      total: 0,

      editingUpload: false,

      form: new window.AppForm(initialState),

      allLogActions: [
        "Ad hoc message/email sent to contact",
        "Ad hoc message/email sent to user",
        "Appointment cancellation messaged to assistant",
        "Appointment cancellation messaged to customer",
        "Appointment cancellation messaged to technician",
        "Appointment cancelled",
        "Appointment creation messaged to assistant",
        "Appointment creation messaged to customer",
        "Appointment creation messaged to technician",
        "Appointment notification emailed to customer",
        "Appointment notification messaged to customer",
        "Appointment travel notification messaged to customer",
        "Appointment update messaged to assistant",
        "Appointment update messaged to customer",
        "Appointment update messaged to technician",
        "Appointment updated",
        "Invoice created",
        "Invoice deleted",
        "Invoice emailed to customer",
        "Invoice paid",
        "Invoice refunded",
        "Invoice SMSed to customer",
        "Invoice unpaid",
        "Invoice updated",
        "Job completed as invoice has been paid",
        "Job created",
        "Job follow-up added",
        "Job referred by manually changed",
        "Job set to Reachable",
        "Job set to Unreachable",
        "Job status manually changed",
        "Job updated",
        "Quote accepted",
        "Quote follow-up messaged to customer",
        "Multiple quotes emailed to CCT",
        "Recurring appointment added",
      ],
    };
  },

  watch: {
    currentJobId() {
      this.init();
    },
  },

  mounted() {
    this.init();

    EventBus.$on("chated-to-contact", () => {
      this.init();
    });
    EventBus.$on("chated-to-user", () => {
      this.init();
    });
    EventBus.$on("appointment-created", () => {
      this.init();
    });
    EventBus.$on("appointment-updated", () => {
      this.init();
    });
    EventBus.$on("appointment-deleted", () => {
      this.init();
    });
    EventBus.$on("invoice-sent", () => {
      this.init();
    });
    EventBus.$on("job-updated", () => {
      this.init();
    });
    EventBus.$on("user-notes-created", () => {
      setTimeout(() => {
        this.init();
      }, 1000);
    });
    EventBus.$on("before-and-after-image-created", () => {
      setTimeout(() => {
        this.init();
      }, 1000);
    });

    EventBus.$on("creating-before-and-after", () => {
      if (this.$refs.beforeAndAfter) {
        this.$refs.beforeAndAfter.showModal();
      }
    });
  },

  methods: {
    init() {
      this.form.reset();
      this.form.job_id = this.currentJobId;

      this.activities = [];
      this.page = 0;
      this.lastPage = null;
      this.loadingMore = false;

      return this.loadMore();
    },

    /* eslint-disable no-param-reassign */
    loadMore() {
      if (this.page < this.lastPage || this.lastPage === null) {
        const page = this.page + 1;
        this.loadingMore = true;

        this.form.startProcessing();

        const limit = this.readonly ? 99999999 : this.perPage;

        // noinspection JSUnresolvedFunction
        this.$http
          .get(`/jobs/${this.currentJobId}/activities?page=${page}&limit=${limit}`)
          .then((response) => {
            // Unpack the JSON in detail property
            if (response.body.data && response.body.data.length > 0) {
              this.page = response.body.current_page;
              this.lastPage = response.body.last_page;
              this.total = response.body.total;

              try {
                response.body.data.forEach((activity, index) => {
                  if (activity.action && activity.detail) {
                    response.body.data[index].detail = JSON.parse(activity.detail);
                    response.body.data[index].type = "log";
                  } else {
                    if (this.allLogActions.includes(activity.action)) {
                      response.body.data[index].type = "log";
                    } else {
                      response.body.data[index].type = "note";
                    }
                  }
                });
              } catch (error) {
                window.toastr.error("There seems to be some problem with activity data.", "Oops");
              }
            }

            this.activities = this.activities.concat(response.body.data);

            this.page = response.body.current_page;
            this.lastPage = response.body.last_page;

            this.initialised = true;
            this.loadingMore = false;
            this.form.finishProcessing();
          })
          .catch(() => {
            this.initialised = true;
            this.loadingMore = false;
            this.form.finishProcessing();
          });
      }
    },

    shouldShow(activity) {
      if (this.type === "all") {
        return true;
      }

      if (this.type === "notes" && activity.type === "note" && activity.detail) {
        return true;
      }

      if (this.type === "files") {
        return this.$lodash.has(activity, "media[0].id");
      }

      if (this.type === "log" && activity.type === "log") {
        if (this.subType === "all") {
          return true;
        }

        return this.subType === this.getEventSubType(activity);
      }

      return false;
    },

    onSubmit() {
      if (!this.form.detail) {
        return;
      }

      this.form.post("/job-notes").then(() => {
        this.$nextTick(() => this.init());

        window.toastr.success("Note added!", "Success");
      });
    },

    getEventSubType(activity) {
      switch (activity.action) {
        case "Appointment added":
        case "Appointment cancellation messaged to customer":
        case "Appointment cancellation messaged to technician":
        case "Appointment cancellation messaged to assistant":
        case "Appointment cancelled":
        case "Appointment creation messaged to customer":
        case "Appointment creation messaged to technician":
        case "Appointment creation messaged to assistant":
        case "Appointment notification emailed to customer":
        case "Appointment notification messaged to customer":
        case "Appointment travel notification messaged to customer":
        case "Appointment update messaged to customer":
        case "Appointment update messaged to technician":
        case "Appointment update messaged to assistant":
        case "Appointment updated":
        case "Recurring appointment added":
          return "appointment";

        case "Invoice created":
        case "Invoice deleted":
        case "Invoice emailed to customer":
        case "Invoice SMSed to customer":
        case "Invoice paid":
        case "Invoice unpaid":
        case "Invoice refunded":
        case "Invoice updated":
        case "Quote accepted":
        case "Quote follow-up messaged to customer":
        case "Multiple quotes emailed to CCT":
          return "invoice";

        case "Ad hoc message/email sent to contact":
        case "Ad hoc message/email sent to user":
        case "Job completed as invoice has been paid":
        case "Job created":
        case "Job updated":
        case "Job follow-up added":
        case "Job referred by manually changed":
        case "Job status manually changed":
        case "Job set to Unreachable":
        case "Job set to Reachable":
        default:
          return "job";
      }
    },

    getOperationClass(activity) {
      switch (activity.action) {
        case "Ad hoc message/email sent to contact":
        case "Ad hoc message/email sent to user":
        case "Appointment added":
        case "Invoice created":
        case "Invoice paid":
        case "Job completed as invoice has been paid":
        case "Job created":
        case "Job follow-up added":
        case "Job set to Unreachable":
        case "Job set to Reachable":
        case "Quote accepted":
        case "Recurring appointment added":
          return "successful";

        case "Appointment updated":
        case "Invoice updated":
        case "Job updated":
          return "update";

        case "Appointment cancelled":
        case "Invoice deleted":
        case "Invoice unpaid":
        case "Invoice refunded":
        case "Job status manually changed":
        case "Job referred by manually changed":
          return "destroy";

        default:
          return "";
      }
    },

    getOperationIcon(activity) {
      switch (activity.action) {
        case "Appointment added":
        case "Recurring appointment added":
          return ["fal", "calendar-plus"];

        case "Ad hoc message/email sent to contact":
        case "Ad hoc message/email sent to user":
        case "Appointment notification emailed to customer":
        case "Invoice emailed to customer":
          return ["fal", "envelope"];

        case "Invoice SMSed to customer":
          return ["fal", "comment-alt-dollar"];

        case "Appointment creation messaged to customer":
        case "Appointment creation messaged to technician":
        case "Appointment creation messaged to assistant":
          return ["fal", "comment-plus"];

        case "Appointment update messaged to customer":
        case "Appointment update messaged to technician":
        case "Appointment update messaged to assistant":
          return ["fal", "comment-edit"];

        case "Appointment cancellation messaged to customer":
        case "Appointment cancellation messaged to technician":
        case "Appointment cancellation messaged to assistant":
          return ["fal", "comment-times"];

        case "Appointment notification messaged to customer":
        case "Appointment travel notification messaged to customer":
        case "Quote follow-up messaged to customer":
        case "Multiple quotes emailed to CCT":
          return ["fal", "comment-exclamation"];

        case "Appointment updated":
          return ["fal", "calendar-edit"];

        case "Invoice updated":
          return ["fal", "file-edit"];

        case "Job updated":
        case "Job status manually changed":
        case "Job referred by manually changed":
          return ["fal", "wrench"];

        case "Appointment cancelled":
          return ["fal", "calendar-times"];

        case "Invoice deleted":
          return ["fal", "file-times"];

        case "Invoice created":
          return ["fal", "file-plus"];

        case "Invoice paid":
          return ["fal", "star-exclamation"];

        case "Quote accepted":
          return ["fal", "thumbs-up"];

        case "Invoice refunded":
        case "Invoice unpaid":
          return ["fal", "undo"];

        case "Job created":
          return ["fal", "plus-hexagon"];

        case "Job completed as invoice has been paid":
          return ["fal", "check-circle"];

        case "Job follow-up added":
        case "Job set to Unreachable":
        case "Job set to Reachable":
          return ["fal", "headset"];

        case null:
          if (activity.media.length > 0) {
            if (
              activity.media[0].mime_type === "image/jpeg" ||
              activity.media[0].mime_type === "image/jpg" ||
              activity.media[0].mime_type === "image/gif" ||
              activity.media[0].mime_type === "image/png"
            ) {
              // Checking file_name, if it's 'signature'
              if (this.$lodash.get(activity.media[0], "file_name").includes("signature")) {
                return ["fal", "signature"];
              }

              return ["fal", "image"];
            }

            return ["fal", "paperclip"];
          }
          return ["fal", "user-edit"];

        default:
          return "";
      }
    },

    fileUploaded() {
      window.toastr.success("Attachment added!", "Success");

      this.init();
    },

    print() {
      window.print();
    },

    exportLog() {
      window.open(`/jobs/${this.currentJobId}/logs`);
    },

    createBeforeAndAfter() {
      if (this.creating) {
        return;
      }

      EventBus.$emit("creating-before-and-after");
    },
  },
};
</script>
